import { useEffect, useRef, useState } from "react";
import "./CarromGame.css";

const BOARD_SIZE = 600;
const HOLE_RADIUS = 40;
const COIN_RADIUS = 18; // Increased from 15
const STRIKER_RADIUS = 25; // Increased from 24
const FRICTION = 0.982;
const WALL_BOUNCE = 0.6;
const BASE_OFFSET = 120;
const BASE_START = 85;
const BASE_END = BOARD_SIZE - BASE_START;

const CarromGame = ({ onBack, isPaused }) => {
    const canvasRef = useRef(null);
    const audioCtxRef = useRef(null);
    const [score, setScore] = useState({ player: 0, ai: 0 });
    const [winner, setWinner] = useState(null);
    const [turn, setTurn] = useState("PLAYER");
    const [msg, setMsg] = useState("Your Turn!");
    const [strikerX, setStrikerX] = useState(BOARD_SIZE / 2);

    const gameRef = useRef({
        striker: { x: BOARD_SIZE / 2, y: BOARD_SIZE - 120, vx: 0, vy: 0 },
        coins: [],
        turn: "PLAYER",
        isDragging: false,
        isSliding: false,
        dragStart: null,
        dragCurrent: null,
        isMoving: false,
        isPaused: false,
        gestureMode: null,
        score: { player: 0, ai: 0 },
        winner: null,
        strokePocketedOwn: false
    });

    const initCoins = () => {
        const coins = [];
        const center = BOARD_SIZE / 2;
        coins.push({ x: center, y: center, vx: 0, vy: 0, type: "queen", color: "#f43f5e" });
        const colors = ["#f8fafc", "#1e293b"];
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI * 2) / 6;
            coins.push({
                x: center + Math.cos(angle) * COIN_RADIUS * 2.1,
                y: center + Math.sin(angle) * COIN_RADIUS * 2.1,
                vx: 0, vy: 0, type: i % 2 === 0 ? "white" : "black", color: colors[i % 2]
            });
        }
        for (let i = 0; i < 12; i++) {
            const angle = (i * Math.PI * 2) / 12;
            coins.push({
                x: center + Math.cos(angle) * COIN_RADIUS * 4.2,
                y: center + Math.sin(angle) * COIN_RADIUS * 4.2,
                vx: 0, vy: 0, type: i % 2 === 0 ? "black" : "white", color: colors[(i + 1) % 2]
            });
        }
        return coins;
    };

    const playSound = (type) => {
        try {
            let actx = audioCtxRef.current;
            if (!actx) {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (!AudioContext) return;
                actx = new AudioContext();
                audioCtxRef.current = actx;
            }
            if (actx.state === "suspended") {
                actx.resume();
            }

            const osc = actx.createOscillator();
            const gain = actx.createGain();
            osc.connect(gain);
            gain.connect(actx.destination);

            const now = actx.currentTime;

            if (type === "collision") {
                osc.type = "sine";
                osc.frequency.setValueAtTime(850, now);
                osc.frequency.exponentialRampToValueAtTime(150, now + 0.04);

                gain.gain.setValueAtTime(0.3, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.035);

                osc.start(now);
                osc.stop(now + 0.04);
            } else if (type === "wall") {
                osc.type = "triangle";
                osc.frequency.setValueAtTime(170, now);
                osc.frequency.exponentialRampToValueAtTime(50, now + 0.07);

                gain.gain.setValueAtTime(0.25, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.07);

                osc.start(now);
                osc.stop(now + 0.08);
            } else if (type === "pocket") {
                osc.type = "sine";
                osc.frequency.setValueAtTime(280, now);
                osc.frequency.exponentialRampToValueAtTime(100, now + 0.14);

                gain.gain.setValueAtTime(0.35, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.14);

                osc.start(now);
                osc.stop(now + 0.15);
            } else if (type === "shoot") {
                osc.type = "triangle";
                osc.frequency.setValueAtTime(110, now);
                osc.frequency.exponentialRampToValueAtTime(550, now + 0.07);

                gain.gain.setValueAtTime(0.12, now);
                gain.gain.exponentialRampToValueAtTime(0.01, now + 0.07);

                osc.start(now);
                osc.stop(now + 0.08);
            }
        } catch (e) {
            console.warn("Dynamic sound play failed:", e);
        }
    };

    const resetGame = () => {
        setScore({ player: 0, ai: 0 });
        setWinner(null);
        setTurn("PLAYER");
        setMsg("Your Turn!");
        const game = gameRef.current;
        game.score = { player: 0, ai: 0 };
        game.winner = null;
        game.coins = initCoins();
        game.striker.x = BOARD_SIZE / 2;
        setStrikerX(BOARD_SIZE / 2);
        game.striker.y = BOARD_SIZE - 120;
        game.striker.vx = 0;
        game.striker.vy = 0;
        game.isDragging = false;
        game.isSliding = false;
        game.gestureMode = null;
        game.dragMode = undefined;
        game.turn = "PLAYER";
        game.strokePocketedOwn = false;
    };

    const handlePlayAgain = () => {
        setWinner(null);
        resetGame();
    };

    useEffect(() => {
        gameRef.current.isPaused = isPaused;
    }, [isPaused]);

    useEffect(() => {
        const game = gameRef.current;
        if (game.coins.length === 0) game.coins = initCoins();

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let animationId;

        const resolve = (p1, p2, r1, r2) => {
            const dx = p2.x - p1.x; const dy = p2.y - p1.y;
            const dist = Math.hypot(dx, dy);
            if (dist < r1 + r2 && dist > 0.1) {
                const nx = dx / dist; const ny = dy / dist;
                const overlap = (r1 + r2) - dist;
                p1.x -= nx * overlap / 2; p1.y -= ny * overlap / 2;
                p2.x += nx * overlap / 2; p2.y += ny * overlap / 2;
                const vrel = (p2.vx - p1.vx) * nx + (p2.vy - p1.vy) * ny;
                if (vrel < 0) {
                    const impulse = -1.8 * vrel / 2;
                    p1.vx -= impulse * nx; p1.vy -= impulse * ny;
                    p2.vx += impulse * nx; p2.vy += impulse * ny;
                    if (vrel < -1.5) {
                        playSound("collision");
                    }
                }
            }
        };

        const update = () => {
            if (gameRef.current.isPaused || gameRef.current.winner) {
                draw(ctx, gameRef.current);
                animationId = requestAnimationFrame(update);
                return;
            }
            let moving = false;
            const updateItem = (item, radius) => {
                if (Math.abs(item.vx) > 0.2 || Math.abs(item.vy) > 0.2) {
                    item.x += item.vx; item.y += item.vy;
                    item.vx *= FRICTION; item.vy *= FRICTION;
                    let hitWall = false;
                    if (item.x < radius + 20) { item.x = radius + 20; item.vx *= -WALL_BOUNCE; hitWall = true; }
                    if (item.x > BOARD_SIZE - radius - 20) { item.x = BOARD_SIZE - radius - 20; item.vx *= -WALL_BOUNCE; hitWall = true; }
                    if (item.y < radius + 20) { item.y = radius + 20; item.vy *= -WALL_BOUNCE; hitWall = true; }
                    if (item.y > BOARD_SIZE - radius - 20) { item.y = BOARD_SIZE - radius - 20; item.vy *= -WALL_BOUNCE; hitWall = true; }
                    if (hitWall && (Math.abs(item.vx) > 1.2 || Math.abs(item.vy) > 1.2)) {
                        playSound("wall");
                    }
                    moving = true;
                } else { item.vx = 0; item.vy = 0; }
            };

            updateItem(game.striker, STRIKER_RADIUS);
            game.coins.forEach(c => updateItem(c, COIN_RADIUS));

            const pockets = [[25, 25], [BOARD_SIZE - 25, 25], [25, BOARD_SIZE - 25], [BOARD_SIZE - 25, BOARD_SIZE - 25]];
            let pocketed = false;
            game.coins = game.coins.filter(c => {
                // Check if coin completely enters the valid corner pocket (outer edge of coin within hole)
                if (pockets.some(([px, py]) => Math.hypot(c.x - px, c.y - py) < (HOLE_RADIUS - COIN_RADIUS))) {
                    const pts = c.type === "queen" ? 50 : c.type === "white" ? 20 : 10;

                    // Award points to the player who pocketed the coin (current shooter)
                    const recipient = game.turn.toLowerCase();

                    const newScore = { ...game.score, [recipient]: game.score[recipient] + pts };
                    game.score = newScore;
                    setScore(newScore);

                    // Track if the shooter pocketed their own coin/queen
                    if (game.turn === "PLAYER" && (c.type === "white" || c.type === "queen")) {
                        game.strokePocketedOwn = true;
                    } else if (game.turn === "AI" && (c.type === "black" || c.type === "queen")) {
                        game.strokePocketedOwn = true;
                    }

                    pocketed = true;
                    playSound("pocket");
                    return false;
                }
                return true;
            });

            game.coins.forEach(c => resolve(game.striker, c, STRIKER_RADIUS, COIN_RADIUS));
            for (let i = 0; i < game.coins.length; i++) {
                for (let j = i + 1; j < game.coins.length; j++) resolve(game.coins[i], game.coins[j], COIN_RADIUS, COIN_RADIUS);
            }

            if (!moving && game.isMoving) {
                if (game.coins.length === 0) {
                    const finalWinner = game.score.player > game.score.ai
                        ? "PLAYER"
                        : game.score.player < game.score.ai
                            ? "AI"
                            : "DRAW";
                    game.winner = finalWinner;
                    setWinner(finalWinner);
                    game.isMoving = false;
                } else {
                    if (!game.strokePocketedOwn) {
                        game.turn = game.turn === "PLAYER" ? "AI" : "PLAYER";
                        setTurn(game.turn);
                        setMsg(game.turn === "PLAYER" ? "Your Turn!" : "AI is Thinking...");
                    } else {
                        setMsg(game.turn === "PLAYER" ? "Nice! Your turn again." : "AI scored! Its turn.");
                    }
                    game.strokePocketedOwn = false;

                    game.striker.x = BOARD_SIZE / 2;
                    setStrikerX(BOARD_SIZE / 2);
                    game.striker.y = game.turn === "PLAYER" ? BOARD_SIZE - 120 : 120;
                    game.striker.vx = 0; game.striker.vy = 0;

                    if (game.turn === "AI") setTimeout(aiShoot, 1500);
                }
            }
            game.isMoving = moving;
            draw(ctx, game);
            animationId = requestAnimationFrame(update);
        };

        const aiShoot = () => {
            if (gameRef.current.coins.length === 0) return;
            const target = gameRef.current.coins[Math.floor(Math.random() * gameRef.current.coins.length)];
            gameRef.current.striker.x = target.x + (Math.random() * 40 - 20);
            setTimeout(() => {
                const dx = target.x - gameRef.current.striker.x;
                const dy = target.y - gameRef.current.striker.y;
                const d = Math.hypot(dx, dy);
                gameRef.current.striker.vx = (dx / d) * (25 + Math.random() * 15);
                gameRef.current.striker.vy = (dy / d) * (25 + Math.random() * 15);
                playSound("shoot");
            }, 500);
        };

        const draw = (ctx, game) => {
            ctx.clearRect(0, 0, BOARD_SIZE, BOARD_SIZE);

            // 1. Natural Polished Birch Wood Board Surface (Radial blend for varnish feel)
            const boardGrad = ctx.createRadialGradient(BOARD_SIZE / 2, BOARD_SIZE / 2, 50, BOARD_SIZE / 2, BOARD_SIZE / 2, BOARD_SIZE * 0.7);
            boardGrad.addColorStop(0, "#f9e7d4"); // Warm maple center glow
            boardGrad.addColorStop(0.4, "#f3d1b0"); // Classic tournament gold birch
            boardGrad.addColorStop(0.85, "#dfa97b"); // Rich varnish transition
            boardGrad.addColorStop(1, "#be8454"); // Dark wood border shade

            ctx.fillStyle = boardGrad;
            ctx.fillRect(40, 40, BOARD_SIZE - 80, BOARD_SIZE - 80);

            // Fine organic circular growth rings (Real wood look)
            ctx.strokeStyle = "rgba(100, 60, 20, 0.015)";
            ctx.lineWidth = 1.5;
            for (let r = 60; r < BOARD_SIZE * 0.7; r += 28) {
                ctx.beginPath();
                for (let a = 0; a < Math.PI * 2; a += 0.1) {
                    const noise = Math.sin(a * 4) * 2.5 + Math.cos(a * 7) * 1.8 + Math.sin(r * 0.05) * 1.2;
                    const rx = BOARD_SIZE / 2 + Math.cos(a) * (r + noise);
                    const ry = BOARD_SIZE / 2 + Math.sin(a) * (r + noise);
                    if (a === 0) ctx.moveTo(rx, ry);
                    else ctx.lineTo(rx, ry);
                }
                ctx.closePath();
                ctx.stroke();
            }

            // Dual fine wavy linear wood grain veins (transverse laminate feel)
            ctx.strokeStyle = "rgba(90, 50, 15, 0.012)";
            ctx.lineWidth = 1;
            for (let y = 50; y < BOARD_SIZE - 50; y += 15) {
                ctx.beginPath();
                ctx.moveTo(40, y);
                ctx.bezierCurveTo(
                    BOARD_SIZE / 3, y + Math.sin(y * 0.05) * 15 - 5,
                    (BOARD_SIZE * 2) / 3, y + Math.cos(y * 0.05) * 15 + 5,
                    BOARD_SIZE - 40, y
                );
                ctx.stroke();
            }

            // Ambient Occlusion Inner Shadows (fading inwards from frame borders to give depth)
            const drawInnerShadow = (gradX1, gradY1, gradX2, gradY2, rectX, rectY, rectW, rectH) => {
                const shadow = ctx.createLinearGradient(gradX1, gradY1, gradX2, gradY2);
                shadow.addColorStop(0, "rgba(0, 0, 0, 0.35)");
                shadow.addColorStop(1, "rgba(0, 0, 0, 0)");
                ctx.fillStyle = shadow;
                ctx.fillRect(rectX, rectY, rectW, rectH);
            };
            // Top Shadow
            drawInnerShadow(0, 40, 0, 85, 40, 40, BOARD_SIZE - 80, 45);
            // Bottom Shadow
            drawInnerShadow(0, BOARD_SIZE - 40, 0, BOARD_SIZE - 85, 40, BOARD_SIZE - 85, BOARD_SIZE - 80, 45);
            // Left Shadow
            drawInnerShadow(40, 0, 85, 0, 40, 40, 45, BOARD_SIZE - 80);
            // Right Shadow
            drawInnerShadow(BOARD_SIZE - 40, 0, BOARD_SIZE - 85, 0, BOARD_SIZE - 85, 40, 45, BOARD_SIZE - 80);

            // 2. Elegantly Inlaid Thin Marquetry Border Lines (Mahogany/Ebony style)
            ctx.strokeStyle = "rgba(76, 29, 4, 0.4)";
            ctx.lineWidth = 2;
            ctx.strokeRect(95, 95, BOARD_SIZE - 190, BOARD_SIZE - 190);

            ctx.strokeStyle = "rgba(76, 29, 4, 0.15)";
            ctx.lineWidth = 1;
            ctx.strokeRect(91, 91, BOARD_SIZE - 182, BOARD_SIZE - 182);

            // 3. Deep Beveled Rosewood Board Frame (4 distinct miter-jointed trapezoidal planks)
            const drawWoodGrainInPlank = (pctx, x1, y1, x2, y2, x3, y3, x4, y4, isVert) => {
                pctx.save();
                pctx.beginPath();
                pctx.moveTo(x1, y1);
                pctx.lineTo(x2, y2);
                pctx.lineTo(x3, y3);
                pctx.lineTo(x4, y4);
                pctx.closePath();
                pctx.clip();

                pctx.strokeStyle = "rgba(75, 25, 10, 0.09)";
                pctx.lineWidth = 1;
                if (isVert) {
                    const minX = Math.min(x1, x2, x3, x4);
                    const maxX = Math.max(x1, x2, x3, x4);
                    for (let x = minX - 10; x < maxX + 10; x += 3) {
                        pctx.beginPath();
                        pctx.moveTo(x, 0);
                        pctx.bezierCurveTo(x + Math.sin(x) * 2, BOARD_SIZE / 3, x - Math.cos(x) * 2, (BOARD_SIZE * 2) / 3, x + Math.sin(x * 1.5) * 3, BOARD_SIZE);
                        pctx.stroke();
                    }
                } else {
                    const minY = Math.min(y1, y2, y3, y4);
                    const maxY = Math.max(y1, y2, y3, y4);
                    for (let y = minY - 10; y < maxY + 10; y += 3) {
                        pctx.beginPath();
                        pctx.moveTo(0, y);
                        pctx.bezierCurveTo(BOARD_SIZE / 3, y + Math.sin(y) * 2, (BOARD_SIZE * 2) / 3, y - Math.cos(y) * 2, BOARD_SIZE, y + Math.sin(y * 1.5) * 3);
                        pctx.stroke();
                    }
                }
                pctx.restore();
            };

            // Top Plank
            const topGrad = ctx.createLinearGradient(0, 0, 0, 40);
            topGrad.addColorStop(0, "#220801"); // Deep edge
            topGrad.addColorStop(0.2, "#401306"); // Bevel slope
            topGrad.addColorStop(0.5, "#6b220d"); // Bevel highlight peak
            topGrad.addColorStop(0.8, "#3d1105"); // Bevel slope inner
            topGrad.addColorStop(1, "#180500"); // Inner dark groove
            ctx.fillStyle = topGrad;
            ctx.beginPath();
            ctx.moveTo(0, 0); ctx.lineTo(BOARD_SIZE, 0); ctx.lineTo(BOARD_SIZE - 40, 40); ctx.lineTo(40, 40);
            ctx.closePath();
            ctx.fill();
            drawWoodGrainInPlank(ctx, 0, 0, BOARD_SIZE, 0, BOARD_SIZE - 40, 40, 40, 40, false);

            // Bottom Plank
            const bottomGrad = ctx.createLinearGradient(0, BOARD_SIZE, 0, BOARD_SIZE - 40);
            bottomGrad.addColorStop(0, "#220801");
            bottomGrad.addColorStop(0.2, "#401306");
            bottomGrad.addColorStop(0.5, "#6b220d");
            bottomGrad.addColorStop(0.8, "#3d1105");
            bottomGrad.addColorStop(1, "#180500");
            ctx.fillStyle = bottomGrad;
            ctx.beginPath();
            ctx.moveTo(0, BOARD_SIZE); ctx.lineTo(BOARD_SIZE, BOARD_SIZE); ctx.lineTo(BOARD_SIZE - 40, BOARD_SIZE - 40); ctx.lineTo(40, BOARD_SIZE - 40);
            ctx.closePath();
            ctx.fill();
            drawWoodGrainInPlank(ctx, 0, BOARD_SIZE, BOARD_SIZE, BOARD_SIZE, BOARD_SIZE - 40, BOARD_SIZE - 40, 40, BOARD_SIZE - 40, false);

            // Left Plank
            const leftGrad = ctx.createLinearGradient(0, 0, 40, 0);
            leftGrad.addColorStop(0, "#220801");
            leftGrad.addColorStop(0.2, "#401306");
            leftGrad.addColorStop(0.5, "#6b220d");
            leftGrad.addColorStop(0.8, "#3d1105");
            leftGrad.addColorStop(1, "#180500");
            ctx.fillStyle = leftGrad;
            ctx.beginPath();
            ctx.moveTo(0, 0); ctx.lineTo(0, BOARD_SIZE); ctx.lineTo(40, BOARD_SIZE - 40); ctx.lineTo(40, 40);
            ctx.closePath();
            ctx.fill();
            drawWoodGrainInPlank(ctx, 0, 0, 0, BOARD_SIZE, 40, BOARD_SIZE - 40, 40, 40, true);

            // Right Plank
            const rightGrad = ctx.createLinearGradient(BOARD_SIZE, 0, BOARD_SIZE - 40, 0);
            rightGrad.addColorStop(0, "#220801");
            rightGrad.addColorStop(0.2, "#401306");
            rightGrad.addColorStop(0.5, "#6b220d");
            rightGrad.addColorStop(0.8, "#3d1105");
            rightGrad.addColorStop(1, "#180500");
            ctx.fillStyle = rightGrad;
            ctx.beginPath();
            ctx.moveTo(BOARD_SIZE, 0); ctx.lineTo(BOARD_SIZE, BOARD_SIZE); ctx.lineTo(BOARD_SIZE - 40, BOARD_SIZE - 40); ctx.lineTo(BOARD_SIZE - 40, 40);
            ctx.closePath();
            ctx.fill();
            drawWoodGrainInPlank(ctx, BOARD_SIZE, 0, BOARD_SIZE, BOARD_SIZE, BOARD_SIZE - 40, BOARD_SIZE - 40, BOARD_SIZE - 40, 40, true);

            // Outer and Inner highlights for high gloss finish
            ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
            ctx.lineWidth = 1.5;
            ctx.strokeRect(1, 1, BOARD_SIZE - 2, BOARD_SIZE - 2); // Outer shine rim

            ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
            ctx.lineWidth = 1;
            ctx.strokeRect(39, 39, BOARD_SIZE - 78, BOARD_SIZE - 78); // Inner shine rim

            // Crisp 45-degree Corner Miter Joints with reflection highlights
            [[0, 0, 40, 40], [BOARD_SIZE, 0, BOARD_SIZE - 40, 40], [0, BOARD_SIZE, 40, BOARD_SIZE - 40], [BOARD_SIZE, BOARD_SIZE, BOARD_SIZE - 40, BOARD_SIZE - 40]].forEach(([x1, y1, x2, y2]) => {
                ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
                ctx.stroke();

                ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x1 + 1.5, y1); ctx.lineTo(x2 + 1.5, y2);
                ctx.stroke();
            });

            // 4. Four Corner Pockets (Holes) with Specular Metallic Brass Rims and Interior Netting
            const pocketCenters = [[25, 25], [BOARD_SIZE - 25, 25], [25, BOARD_SIZE - 25], [BOARD_SIZE - 25, BOARD_SIZE - 25]];
            pocketCenters.forEach(([x, y]) => {
                // Highly polished metallic brass rim (radial gradient with specular bands)
                const brassGrad = ctx.createRadialGradient(x, y, HOLE_RADIUS - 4, x, y, HOLE_RADIUS + 6);
                brassGrad.addColorStop(0, "#7a6021");
                brassGrad.addColorStop(0.25, "#d9b343");
                brassGrad.addColorStop(0.48, "#fff6cf"); // Gleaming highlight
                brassGrad.addColorStop(0.7, "#d9b343");
                brassGrad.addColorStop(0.88, "#7a6021");
                brassGrad.addColorStop(1, "#362709");

                ctx.fillStyle = brassGrad;
                ctx.beginPath();
                ctx.arc(x, y, HOLE_RADIUS + 6, 0, Math.PI * 2);
                ctx.fill();

                // Heavy inner shadow rim for pocket depth
                ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
                ctx.lineWidth = 2.5;
                ctx.beginPath();
                ctx.arc(x, y, HOLE_RADIUS, 0, Math.PI * 2);
                ctx.stroke();

                // Dark 3D interior cavern
                const holeGrad = ctx.createRadialGradient(x, y, HOLE_RADIUS * 0.15, x, y, HOLE_RADIUS);
                holeGrad.addColorStop(0, "#000000");
                holeGrad.addColorStop(0.78, "#0e0e0e");
                holeGrad.addColorStop(0.95, "#1e1e1e");
                holeGrad.addColorStop(1, "#333333");

                ctx.fillStyle = holeGrad;
                ctx.beginPath();
                ctx.arc(x, y, HOLE_RADIUS, 0, Math.PI * 2);
                ctx.fill();

                // Mesh netting detail inside pocket (Intersecting arcs for 3D net basket)
                ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
                ctx.lineWidth = 1;
                for (let r = 8; r < HOLE_RADIUS; r += 8) {
                    ctx.beginPath();
                    ctx.arc(x, y, r, 0, Math.PI * 2);
                    ctx.stroke();
                }
                for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                    ctx.lineTo(x + Math.cos(a) * HOLE_RADIUS, y + Math.sin(a) * HOLE_RADIUS);
                    ctx.stroke();
                }
            });

            // 5. Symmetric Decorative Board Graphics & Concentric Center Medallion (Center Rose Mandala)
            const cx = BOARD_SIZE / 2;
            const cy = BOARD_SIZE / 2;

            // Outer geometric red decorative ring
            ctx.strokeStyle = "#ae0a0b";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(cx, cy, 88, 0, Math.PI * 2);
            ctx.stroke();

            // Fine gold inlay accent ring
            ctx.strokeStyle = "#c5a85c";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(cx, cy, 83, 0, Math.PI * 2);
            ctx.stroke();

            // Gold dashed accent details
            ctx.strokeStyle = "rgba(197, 168, 92, 0.6)";
            ctx.lineWidth = 1.5;
            ctx.setLineDash([4, 6]);
            ctx.beginPath();
            ctx.arc(cx, cy, 79, 0, Math.PI * 2);
            ctx.stroke();
            ctx.setLineDash([]);

            // Inner dark band
            ctx.strokeStyle = "#1e293b";
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(cx, cy, 73, 0, Math.PI * 2);
            ctx.stroke();

            // 16 Symmetrical Star Points (Beautiful Mandala Petals)
            for (let i = 0; i < 16; i++) {
                const angle = (i * Math.PI * 2) / 16;
                const nextAngle = ((i + 1) * Math.PI * 2) / 16;
                const midAngle = angle + (Math.PI / 16);

                // Peak coordinate
                const px1 = cx + Math.cos(midAngle) * 73;
                const py1 = cy + Math.sin(midAngle) * 73;
                // Base coordinates
                const px2 = cx + Math.cos(angle) * 48;
                const py2 = cy + Math.sin(angle) * 48;
                const px3 = cx + Math.cos(nextAngle) * 48;
                const py3 = cy + Math.sin(nextAngle) * 48;

                ctx.fillStyle = i % 2 === 0 ? "#ae0a0b" : "#2d3748";
                ctx.beginPath();
                ctx.moveTo(px2, py2);
                ctx.lineTo(px1, py1);
                ctx.lineTo(px3, py3);
                ctx.closePath();
                ctx.fill();

                ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
                ctx.lineWidth = 0.5;
                ctx.stroke();
            }

            // Concentric red inner band
            ctx.strokeStyle = "#ae0a0b";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(cx, cy, 48, 0, Math.PI * 2);
            ctx.stroke();

            // Inner gold ring border
            ctx.strokeStyle = "#c5a85c";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(cx, cy, 42, 0, Math.PI * 2);
            ctx.stroke();

            // Center Medallion Wood core
            ctx.fillStyle = "#2a0d01";
            ctx.beginPath();
            ctx.arc(cx, cy, 41, 0, Math.PI * 2);
            ctx.fill();

            // Core 8-pointed gold star
            ctx.fillStyle = "#c5a85c";
            for (let i = 0; i < 8; i++) {
                const angle = (i * Math.PI * 2) / 8;
                const px1 = cx + Math.cos(angle) * 35;
                const py1 = cy + Math.sin(angle) * 35;
                const px2 = cx + Math.cos(angle - 0.25) * 12;
                const py2 = cy + Math.sin(angle - 0.25) * 12;
                const px3 = cx + Math.cos(angle + 0.25) * 12;
                const py3 = cy + Math.sin(angle + 0.25) * 12;

                ctx.beginPath();
                ctx.moveTo(px2, py2);
                ctx.lineTo(px1, py1);
                ctx.lineTo(px3, py3);
                ctx.closePath();
                ctx.fill();
            }

            // Center Medallion Central red eye
            ctx.fillStyle = "#ae0a0b";
            ctx.beginPath();
            ctx.arc(cx, cy, 14, 0, Math.PI * 2);
            ctx.fill();

            // Central solid gold screw/pin bead with spec shine
            const goldPinGrad = ctx.createRadialGradient(cx - 2, cy - 2, 1, cx, cy, 6);
            goldPinGrad.addColorStop(0, "#fff5dc");
            goldPinGrad.addColorStop(0.4, "#d99d26");
            goldPinGrad.addColorStop(1, "#6b4507");
            ctx.fillStyle = goldPinGrad;
            ctx.beginPath();
            ctx.arc(cx, cy, 6.5, 0, Math.PI * 2);
            ctx.fill();

            // Symmetrical Diagonal pointer lines with red/gold arrowhead decals
            ctx.strokeStyle = "rgba(174, 10, 11, 0.4)";
            ctx.lineWidth = 1.5;
            [[70, 70], [BOARD_SIZE - 70, 70], [70, BOARD_SIZE - 70], [BOARD_SIZE - 70, BOARD_SIZE - 70]].forEach(([x, y]) => {
                const dx = BOARD_SIZE / 2 - x;
                const dy = BOARD_SIZE / 2 - y;
                const len = Math.hypot(dx, dy);
                const targetX = x + (dx / len) * 155;
                const targetY = y + (dy / len) * 155;

                ctx.beginPath();
                ctx.moveTo(x + (dx / len) * 30, y + (dy / len) * 30);
                ctx.lineTo(targetX, targetY);
                ctx.stroke();

                // Detailed Gold arrow circles with red core centers
                ctx.fillStyle = "#c5a85c";
                ctx.beginPath();
                ctx.arc(targetX, targetY, 7.5, 0, Math.PI * 2);
                ctx.fill();

                ctx.fillStyle = "#ae0a0b";
                ctx.beginPath();
                ctx.arc(targetX, targetY, 4, 0, Math.PI * 2);
                ctx.fill();
            });

            // 6. Premium Inlaid Ivory/Gold Striker Baselines (Top, Bottom, Left, Right)
            const drawBaseline = (x1, y1, x2, y2, isVert) => {
                // Inlaid Ivory strip background
                const ivoryGrad = ctx.createLinearGradient(x1, y1, x2, y2);
                ivoryGrad.addColorStop(0, "#fafaf9");
                ivoryGrad.addColorStop(0.5, "#f3f3ee");
                ivoryGrad.addColorStop(1, "#e6e5e0");

                ctx.fillStyle = ivoryGrad;
                ctx.strokeStyle = "#c5a85c"; // Elegant gold inlay border
                ctx.lineWidth = 1.5;

                ctx.beginPath();
                if (isVert) {
                    ctx.rect(x1 - 10, y1, 20, y2 - y1);
                } else {
                    ctx.rect(x1, y1 - 10, x2 - x1, 20);
                }
                ctx.fill();
                ctx.stroke();

                // Inner Crimson baseline strip indicator
                ctx.strokeStyle = "#ae0a0b";
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                if (isVert) {
                    ctx.moveTo(x1, y1); ctx.lineTo(x1, y2);
                } else {
                    ctx.moveTo(x1, y1); ctx.lineTo(x2, y1);
                }
                ctx.stroke();

                // Symmetrical baseline indicator caps in high-gloss mahogany/crimson
                [[x1, y1], [x2, y2]].forEach(([cx, cy]) => {
                    // Soft drop shadow under baseline caps
                    ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
                    ctx.beginPath();
                    ctx.arc(cx, cy + 1.5, 14, 0, Math.PI * 2);
                    ctx.fill();

                    // Capsule Body Gradient
                    const capGrad = ctx.createRadialGradient(cx - 3, cy - 3, 2, cx, cy, 14);
                    capGrad.addColorStop(0, "#fda4af");
                    capGrad.addColorStop(0.3, "#f43f5e");
                    capGrad.addColorStop(0.9, "#9f1239");
                    capGrad.addColorStop(1, "#4c0519");

                    ctx.fillStyle = capGrad;
                    ctx.beginPath();
                    ctx.arc(cx, cy, 14, 0, Math.PI * 2);
                    ctx.fill();

                    // Concentric gold foil ring
                    ctx.strokeStyle = "#c5a85c";
                    ctx.lineWidth = 1.5;
                    ctx.beginPath();
                    ctx.arc(cx, cy, 9, 0, Math.PI * 2);
                    ctx.stroke();

                    // Central glowing gold rivet center
                    const rivetGrad = ctx.createRadialGradient(cx - 1, cy - 1, 0.5, cx, cy, 4);
                    rivetGrad.addColorStop(0, "#ffe4a0");
                    rivetGrad.addColorStop(1, "#b45309");
                    ctx.fillStyle = rivetGrad;
                    ctx.beginPath();
                    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
                    ctx.fill();
                });
            };

            // Render baseline systems using outer constants

            // Render all 4 baseline systems
            drawBaseline(BASE_START, BOARD_SIZE - BASE_OFFSET, BASE_END, BOARD_SIZE - BASE_OFFSET, false); // Bottom
            drawBaseline(BASE_START, BASE_OFFSET, BASE_END, BASE_OFFSET, false); // Top
            drawBaseline(BASE_OFFSET, BASE_START, BASE_OFFSET, BASE_END, true); // Left
            drawBaseline(BOARD_SIZE - BASE_OFFSET, BASE_START, BOARD_SIZE - BASE_OFFSET, BASE_END, true); // Right

            // 7. Translucent Diagonal Studio Varnish Lacquer Sheen Overlay (Over court surface, under coins)
            const sheenGrad = ctx.createLinearGradient(40, 40, BOARD_SIZE - 40, BOARD_SIZE - 40);
            sheenGrad.addColorStop(0, "rgba(255, 255, 255, 0)");
            sheenGrad.addColorStop(0.35, "rgba(255, 255, 255, 0)");
            sheenGrad.addColorStop(0.48, "rgba(255, 255, 255, 0.05)");
            sheenGrad.addColorStop(0.5, "rgba(255, 255, 255, 0.15)"); // Studio spotlight light slash
            sheenGrad.addColorStop(0.52, "rgba(255, 255, 255, 0.05)");
            sheenGrad.addColorStop(0.65, "rgba(255, 255, 255, 0)");
            sheenGrad.addColorStop(1, "rgba(255, 255, 255, 0)");

            ctx.fillStyle = sheenGrad;
            ctx.fillRect(40, 40, BOARD_SIZE - 80, BOARD_SIZE - 80);

            // 8. Render Premium 3D Wooden/Composite Coins (Advanced Spherical Gloss & Concentric Grooves)
            game.coins.forEach(c => {
                const cx = c.x;
                const cy = c.y;
                const isQueen = c.type === "queen";

                // Directional 3D drop shadow (shifts dynamically from center of board)
                const sOffsetDX = (cx - BOARD_SIZE / 2) * 0.025;
                const sOffsetDY = (cy - BOARD_SIZE / 2) * 0.025;
                ctx.fillStyle = "rgba(0, 0, 0, 0.42)";
                ctx.beginPath();
                ctx.arc(cx + sOffsetDX, cy + sOffsetDY + 2, COIN_RADIUS, 0, Math.PI * 2);
                ctx.fill();

                // 3D Spherical gradient body
                const coinGrad = ctx.createRadialGradient(cx - COIN_RADIUS * 0.3, cy - COIN_RADIUS * 0.3, COIN_RADIUS * 0.08, cx, cy, COIN_RADIUS);
                if (isQueen) {
                    coinGrad.addColorStop(0, "#ffe4e6"); // Bright glaze highlight
                    coinGrad.addColorStop(0.2, "#fda4af"); // Soft rose glare
                    coinGrad.addColorStop(0.5, "#f43f5e"); // Rich cherry crimson queen
                    coinGrad.addColorStop(0.88, "#9f1239"); // Deep shade edge
                    coinGrad.addColorStop(1, "#4c0519"); // Dark shadow rim
                } else if (c.type === "white") {
                    coinGrad.addColorStop(0, "#ffffff"); // Pure white specular glow
                    coinGrad.addColorStop(0.35, "#fafaf6"); // Cream highlight
                    coinGrad.addColorStop(0.65, "#eae3cf"); // Rich polished ivory
                    coinGrad.addColorStop(0.9, "#cebf9a"); // Natural composite shadow
                    coinGrad.addColorStop(1, "#8b7e5c"); // Dark wood composite base
                } else {
                    // Charcoal Black coin
                    coinGrad.addColorStop(0, "#8290a6"); // Bright graphite glint
                    coinGrad.addColorStop(0.32, "#475569"); // Charcoal slate body
                    coinGrad.addColorStop(0.72, "#1e293b"); // Midnight black
                    coinGrad.addColorStop(0.92, "#0f172a"); // Dark rim shading
                    coinGrad.addColorStop(1, "#020617"); // Dark core edge
                }

                ctx.fillStyle = coinGrad;
                ctx.beginPath();
                ctx.arc(cx, cy, COIN_RADIUS, 0, Math.PI * 2);
                ctx.fill();

                // Outward beveled coin shoulder edge highlight
                ctx.strokeStyle = isQueen ? "rgba(254, 240, 138, 0.48)" : c.type === "white" ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.18)";
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(cx, cy, COIN_RADIUS - 0.75, 0, Math.PI * 2);
                ctx.stroke();

                // Premium Concentric Grip Grooves
                ctx.strokeStyle = isQueen ? "rgba(254, 240, 138, 0.4)" : c.type === "white" ? "rgba(71, 85, 105, 0.18)" : "rgba(255, 255, 255, 0.18)";
                ctx.lineWidth = 1.2;

                ctx.beginPath();
                ctx.arc(cx, cy, COIN_RADIUS * 0.72, 0, Math.PI * 2);
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(cx, cy, COIN_RADIUS * 0.46, 0, Math.PI * 2);
                ctx.stroke();

                // Detailed Center decals for tournament feel
                if (isQueen) {
                    // Symmetrical 8-star pattern in gold
                    ctx.strokeStyle = "rgba(254, 240, 138, 0.85)";
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    for (let a = 0; a < Math.PI * 2; a += Math.PI / 4) {
                        ctx.moveTo(cx + Math.cos(a) * (COIN_RADIUS * 0.22), cy + Math.sin(a) * (COIN_RADIUS * 0.22));
                        ctx.lineTo(cx + Math.cos(a) * (COIN_RADIUS * 0.46), cy + Math.sin(a) * (COIN_RADIUS * 0.46));
                    }
                    ctx.stroke();

                    ctx.fillStyle = "#fef08a";
                    ctx.beginPath();
                    ctx.arc(cx, cy, COIN_RADIUS * 0.22, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    // Subtly debossed inner center bead
                    ctx.fillStyle = c.type === "white" ? "rgba(255, 255, 255, 0.4)" : "rgba(255, 255, 255, 0.06)";
                    ctx.beginPath();
                    ctx.arc(cx, cy, COIN_RADIUS * 0.22, 0, Math.PI * 2);
                    ctx.fill();
                }

                // High specular glaze shine (glass coat simulation)
                const highlightGrad = ctx.createRadialGradient(cx - COIN_RADIUS * 0.35, cy - COIN_RADIUS * 0.35, 1, cx - COIN_RADIUS * 0.35, cy - COIN_RADIUS * 0.35, COIN_RADIUS * 0.6);
                highlightGrad.addColorStop(0, "rgba(255, 255, 255, 0.5)");
                highlightGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
                ctx.fillStyle = highlightGrad;
                ctx.beginPath();
                ctx.arc(cx - COIN_RADIUS * 0.35, cy - COIN_RADIUS * 0.35, COIN_RADIUS * 0.5, 0, Math.PI * 2);
                ctx.fill();
            });

            // 9. Heavy Polished Transparent Resin/Acrylic Striker (Deep Gold or Ruby Glow with Brass Rims)
            const sx = game.striker.x;
            const sy = game.striker.y;
            const isPlayerStriker = game.turn === "PLAYER";

            // Heavy drop shadow (lifts striker visually off the board)
            ctx.fillStyle = "rgba(0, 0, 0, 0.52)";
            ctx.beginPath();
            ctx.arc(sx + 3, sy + 5, STRIKER_RADIUS + 1, 0, Math.PI * 2);
            ctx.fill();

            // Elegant outer metallic gold rim on the striker
            const strikerBrass = ctx.createRadialGradient(sx, sy, STRIKER_RADIUS - 3, sx, sy, STRIKER_RADIUS);
            strikerBrass.addColorStop(0, "#d99f26");
            strikerBrass.addColorStop(0.5, "#fff2bd"); // specular metal
            strikerBrass.addColorStop(0.8, "#d99f26");
            strikerBrass.addColorStop(1, "#40310c");

            ctx.fillStyle = strikerBrass;
            ctx.beginPath();
            ctx.arc(sx, sy, STRIKER_RADIUS, 0, Math.PI * 2);
            ctx.fill();

            // Translucent glass/acrylic core body
            const strikerGrad = ctx.createRadialGradient(sx - STRIKER_RADIUS * 0.28, sy - STRIKER_RADIUS * 0.28, STRIKER_RADIUS * 0.04, sx, sy, STRIKER_RADIUS - 2);
            if (isPlayerStriker) {
                strikerGrad.addColorStop(0, "#fffdde"); // Glowing golden core
                strikerGrad.addColorStop(0.28, "#fbbf24"); // Polished gold resin
                strikerGrad.addColorStop(0.78, "#d97706"); // Translucent honey acrylic
                strikerGrad.addColorStop(0.95, "#92400e"); // Deep warm shade edge
                strikerGrad.addColorStop(1, "#451a03"); // Heavy dark rim
            } else {
                strikerGrad.addColorStop(0, "#ffe4e6"); // Bright AI white center
                strikerGrad.addColorStop(0.3, "#fda4af"); // Glass rose highlight
                strikerGrad.addColorStop(0.72, "#f43f5e"); // AI pink rose resin
                strikerGrad.addColorStop(0.92, "#be123c"); // Deep AI ruby red
                strikerGrad.addColorStop(1, "#5c061b"); // AI dark base rim
            }

            ctx.fillStyle = strikerGrad;
            ctx.beginPath();
            ctx.arc(sx, sy, STRIKER_RADIUS - 2.5, 0, Math.PI * 2);
            ctx.fill();

            // Concentric grip grooves on striker
            ctx.strokeStyle = "rgba(255, 255, 255, 0.42)";
            ctx.lineWidth = 1.8;
            ctx.beginPath();
            ctx.arc(sx, sy, STRIKER_RADIUS * 0.76, 0, Math.PI * 2);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(sx, sy, STRIKER_RADIUS * 0.45, 0, Math.PI * 2);
            ctx.stroke();

            // Aiming lines and markers inside striker body
            ctx.strokeStyle = isPlayerStriker ? "rgba(254, 240, 138, 0.55)" : "rgba(254, 205, 211, 0.55)";
            ctx.lineWidth = 2.2;
            for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
                ctx.beginPath();
                ctx.moveTo(sx + Math.cos(angle) * (STRIKER_RADIUS * 0.45), sy + Math.sin(angle) * (STRIKER_RADIUS * 0.45));
                ctx.lineTo(sx + Math.cos(angle) * (STRIKER_RADIUS * 0.76), sy + Math.sin(angle) * (STRIKER_RADIUS * 0.76));
                ctx.stroke();
            }

            // Glowing Golden Tournament 4-point Star Emblem in the Striker Center Core
            const drawStrikerCoreStar = (cx, cy, rOuter, rInner) => {
                ctx.save();
                ctx.fillStyle = "#fff2bd";
                // Soft glow shadow for tournament feel
                ctx.shadowColor = "#f59e0b";
                ctx.shadowBlur = 8;
                ctx.beginPath();
                for (let i = 0; i < 8; i++) {
                    const angle = (i * Math.PI) / 4;
                    const r = i % 2 === 0 ? rOuter : rInner;
                    const px = cx + Math.cos(angle) * r;
                    const py = cy + Math.sin(angle) * r;
                    if (i === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }
                ctx.closePath();
                ctx.fill();

                ctx.strokeStyle = "#b45309";
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            };
            drawStrikerCoreStar(sx, sy, 10, 4);

            // High glossy specular glaze on striker (3D crystal shell simulation)
            const strikerGlare = ctx.createRadialGradient(sx - STRIKER_RADIUS * 0.35, sy - STRIKER_RADIUS * 0.35, 1, sx - STRIKER_RADIUS * 0.35, sy - STRIKER_RADIUS * 0.35, STRIKER_RADIUS * 0.75);
            strikerGlare.addColorStop(0, "rgba(255, 255, 255, 0.65)");
            strikerGlare.addColorStop(0.3, "rgba(255, 255, 255, 0.22)");
            strikerGlare.addColorStop(1, "rgba(255, 255, 255, 0)");
            ctx.fillStyle = strikerGlare;
            ctx.beginPath();
            ctx.arc(sx - STRIKER_RADIUS * 0.35, sy - STRIKER_RADIUS * 0.35, STRIKER_RADIUS * 0.7, 0, Math.PI * 2);
            ctx.fill();

            // 10. Glow-in-the-dark Neon Aiming Vector with Dynamic Power Color and Targets
            if (game.isDragging && game.dragStart && game.dragCurrent) {
                const dx = game.dragStart.x - game.dragCurrent.x;
                const dy = game.dragStart.y - game.dragCurrent.y;
                const d = Math.hypot(dx, dy);
                if (d > 5) {
                    // Shot Power calculation to determine glow color dynamically
                    let glowColor = "rgba(56, 189, 248, 0.9)"; // Soft Neon Cyan (Default/Low Power)
                    let glowShadow = "rgba(56, 189, 248, 0.85)";

                    if (d > 75) {
                        glowColor = "rgba(244, 63, 94, 0.98)"; // High Power (Glow Carmine Red)
                        glowShadow = "rgba(244, 63, 94, 0.85)";
                    } else if (d > 30) {
                        glowColor = "rgba(251, 191, 36, 0.95)"; // Medium Power (Bright Glow Yellow)
                        glowShadow = "rgba(251, 191, 36, 0.85)";
                    }

                    // Charge-up neon halo ring around the striker itself
                    ctx.shadowColor = glowShadow;
                    ctx.shadowBlur = 15;
                    ctx.strokeStyle = glowColor;
                    ctx.lineWidth = 3;
                    ctx.beginPath();
                    ctx.arc(sx, sy, STRIKER_RADIUS + 3, 0, Math.PI * 2);
                    ctx.stroke();

                    // Dotted/Dashed Laser aiming line with glow
                    ctx.setLineDash([8, 4]);
                    ctx.strokeStyle = glowColor;
                    ctx.lineWidth = 4;

                    ctx.beginPath();
                    ctx.moveTo(sx, sy);
                    const aimX = sx + dx * 4.2;
                    const aimY = sy + dy * 4.2;
                    ctx.lineTo(aimX, aimY);
                    ctx.stroke();

                    ctx.setLineDash([]);
                    ctx.shadowBlur = 0; // Reset shadow immediately

                    // Double Concentric Aiming Target Crosshair
                    ctx.fillStyle = glowColor;
                    ctx.strokeStyle = "#ffffff";
                    ctx.lineWidth = 2.5;

                    ctx.beginPath();
                    ctx.arc(aimX, aimY, 8, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.stroke();

                    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
                    ctx.beginPath();
                    ctx.arc(aimX, aimY, 17, 0, Math.PI * 2);
                    ctx.stroke();

                    // Aiming Crosshairs tick lines
                    ctx.strokeStyle = "#ffffff";
                    ctx.lineWidth = 1.5;
                    [[0, -6], [0, 6], [-6, 0], [6, 0]].forEach(([tx, ty]) => {
                        ctx.beginPath();
                        ctx.moveTo(aimX + tx, aimY + ty);
                        ctx.lineTo(aimX + tx * 2, aimY + ty * 2);
                        ctx.stroke();
                    });
                }
            }
        };
        update();
        return () => cancelAnimationFrame(animationId);
    }, []);

    const getPos = (e) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();

        let clientX = e.clientX;
        let clientY = e.clientY;

        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else if (e.changedTouches && e.changedTouches.length > 0) {
            clientX = e.changedTouches[0].clientX;
            clientY = e.changedTouches[0].clientY;
        }

        clientX = clientX ?? e.pageX;
        clientY = clientY ?? e.pageY;

        if (clientX === undefined || clientY === undefined) {
            return { x: BOARD_SIZE / 2, y: BOARD_SIZE - 120 };
        }

        return {
            x: (clientX - rect.left) * (BOARD_SIZE / rect.width),
            y: (clientY - rect.top) * (BOARD_SIZE / rect.height)
        };
    };

    const handleStart = (e) => {
        const game = gameRef.current;
        if (game.isMoving || game.turn !== "PLAYER") return;

        const { x, y } = getPos(e);

        // Touch starts in baseline region or bottom half of the board
        if (y > BOARD_SIZE * 0.55) {
            game.touchStart = { x, y };
            game.strikerStartX = game.striker.x;

            const dist = Math.hypot(x - game.striker.x, y - game.striker.y);
            const onStriker = dist <= STRIKER_RADIUS * 2.2;

            if (onStriker) {
                // Touched directly on striker - wait to see if they pull down (aim) or sideways (slide)
                game.gestureMode = "pending";
                game.isSliding = false;
                game.isDragging = false;
            } else {
                // Touched empty baseline - snap and lock into sliding immediately
                game.gestureMode = "sliding";
                game.isSliding = true;
                game.isDragging = false;
                game.striker.x = Math.max(BASE_START, Math.min(BASE_END, x));
                setStrikerX(game.striker.x);
            }
            if (e.cancelable) e.preventDefault();
        }
    };

    useEffect(() => {
        const handleMove = (e) => {
            const game = gameRef.current;
            if (game.isMoving || game.turn !== "PLAYER") return;
            if (!game.gestureMode) return;

            if (e.cancelable) e.preventDefault();

            const { x, y } = getPos(e);
            const dx = x - game.touchStart.x;
            const dy = y - game.touchStart.y;

            // 1. Gesture mode decision logic if state is pending
            if (game.gestureMode === "pending") {
                if (dy > 8 && dy > Math.abs(dx) * 0.6) {
                    // Lock into aiming mode
                    game.gestureMode = "aiming";
                    game.isDragging = true;
                    game.isSliding = false;
                    game.dragStart = { x: game.striker.x, y: game.touchStart.y };
                    game.dragCurrent = { x, y };
                } else if (Math.abs(dx) > 8) {
                    // Lock into sliding mode
                    game.gestureMode = "sliding";
                    game.isSliding = true;
                    game.isDragging = false;
                }
            }

            // 2. Perform actions according to the locked gesture mode
            if (game.gestureMode === "sliding") {
                const distAtStart = Math.hypot(game.touchStart.x - game.strikerStartX, game.touchStart.y - (BOARD_SIZE - 120));
                if (distAtStart < STRIKER_RADIUS * 2.2) {
                    // Relative drag if they started dragging the striker directly (no jumping)
                    game.striker.x = Math.max(BASE_START, Math.min(BASE_END, game.strikerStartX + dx));
                } else {
                    // Absolute positioning if they tapped elsewhere on the baseline
                    game.striker.x = Math.max(BASE_START, Math.min(BASE_END, x));
                }
                setStrikerX(game.striker.x);
            } else if (game.gestureMode === "aiming") {
                game.dragCurrent = { x, y };
            }
        };

        const handleEnd = () => {
            const game = gameRef.current;
            if (game.gestureMode === "aiming" && game.dragCurrent && game.dragStart) {
                const dx = game.dragStart.x - game.dragCurrent.x;
                const dy = game.dragStart.y - game.dragCurrent.y;
                const d = Math.hypot(dx, dy);
                if (d > 10) {
                    const power = Math.min(52, d / 1.5);
                    game.striker.vx = (dx / d) * power;
                    game.striker.vy = (dy / d) * power;
                    playSound("shoot");
                }
            }
            // Reset all state indicators
            game.gestureMode = null;
            game.isDragging = false;
            game.isSliding = false;
            game.dragStart = null;
            game.dragCurrent = null;
            game.touchStart = null;
        };

        window.addEventListener("mousemove", handleMove, { passive: false });
        window.addEventListener("mouseup", handleEnd);
        window.addEventListener("touchmove", handleMove, { passive: false });
        window.addEventListener("touchend", handleEnd);
        window.addEventListener("touchcancel", handleEnd);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleEnd);
            window.removeEventListener("touchmove", handleMove);
            window.removeEventListener("touchend", handleEnd);
            window.removeEventListener("touchcancel", handleEnd);
        };
    }, []);

    return (
        <div className="carrom-container">
            <div className="carrom-layout-main">
                {/* Floating/Sticky Navigation & Score Header */}
                <div className="carrom-header">
                    <button onClick={onBack} className="carrom-back-btn">← HUB</button>
                    <div className="carrom-scores">
                        <div className="side-score-box player">
                            <span className="label">YOU</span>
                            <span className="value">{score.player}</span>
                        </div>
                        <div className="side-message-box">
                            <div className={`turn-dot ${turn === "PLAYER" ? "is-player" : "is-ai"}`}></div>
                            <div className="message-text">{msg}</div>
                        </div>
                        <div className="side-score-box ai">
                            <span className="label">AI</span>
                            <span className="value">{score.ai}</span>
                        </div>
                    </div>
                </div>

                {/* Core Play Area with Visual Position Slider */}
                <div className="carrom-board-container" style={{
                    gridArea: "board",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%"
                }}>
                    <div className="carrom-board-wrap">
                        <canvas ref={canvasRef} width={BOARD_SIZE} height={BOARD_SIZE}
                            onMouseDown={handleStart} onTouchStart={handleStart}
                        />
                    </div>

                    {/* Visual Slider removed - direct striker sliding enabled on board */}
                </div>
                                                
                {/* Footer Actions */}
                <div className="carrom-footer">
                    <button onClick={resetGame} className="side-btn reset">RESET GAME</button>

                    {/* Developer shortcuts to easily test the victory/defeat pop-ups */}
                    {/* <div className="carrom-dev-actions">
                        <button onClick={() => { setWinner("PLAYER"); gameRef.current.winner = "PLAYER"; }} className="dev-test-btn win" title="Test Player Victory">🏆 TEST WIN</button>
                        <button onClick={() => { setWinner("AI"); gameRef.current.winner = "AI"; }} className="dev-test-btn lose" title="Test AI Victory / Defeat">🤖 TEST DEFEAT</button>
                    </div> */}

                    <div className="carrom-hint-side">PULL BACK TO AIM • RELEASE TO SHOOT</div>
                </div>
            </div>

            {/* Premium Interactive Winning / Game Over Modal Overlay */}
            {winner && (
                <div className="carrom-modal-overlay">
                    <div className={`carrom-modal-card ${winner.toLowerCase()}`}>
                        <div className="modal-shine"></div>

                        <div className="modal-badge-container">
                            <div className="modal-badge">
                                {winner === "PLAYER" ? (
                                    <svg className="badge-svg trophy" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                        <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                        <path d="M4 22h16" />
                                        <path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" />
                                        <path d="M12 2a6 6 0 0 1 6 6v5a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8a6 6 0 0 1 6-6z" />
                                    </svg>
                                ) : winner === "AI" ? (
                                    <svg className="badge-svg lose-skull" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M8 15h8" />
                                        <path d="M9 9h.01M15 9h.01" />
                                    </svg>
                                ) : (
                                    <svg className="badge-svg draw-hand" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                                    </svg>
                                )}
                            </div>
                        </div>

                        <h1 className="modal-title">
                            {winner === "PLAYER" ? "VICTORY!" : winner === "AI" ? "DEFEAT" : "MATCH TIED"}
                        </h1>

                        <p className="modal-subtitle">
                            {winner === "PLAYER"
                                ? "You dominated the board with ultimate precision!"
                                : winner === "AI"
                                    ? "The AI calculated a better trajectory this time."
                                    : "An equal battle of skill and strategy!"}
                        </p>

                        <div className="modal-score-board">
                            <div className="score-item player">
                                <span className="score-label">YOUR SCORE</span>
                                <span className="score-num">{score.player}</span>
                            </div>
                            <div className="score-divider">VS</div>
                            <div className="score-item ai">
                                <span className="score-label">AI SCORE</span>
                                <span className="score-num">{score.ai}</span>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button onClick={handlePlayAgain} className="modal-btn play-again">
                                PLAY AGAIN
                                <span className="btn-glow"></span>
                            </button>
                            <button onClick={onBack} className="modal-btn hub">
                                BACK TO HUB
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarromGame;